import { useState, useEffect, useRef } from 'react';
import { createTool } from '../../utils/api';
import { useToast } from '../../context/ToastContext';

const WELCOME = {
  role: 'assistant',
  content: "Tell me what tool you'd like to add — brand, type, and anything you know about it. I'll ask a couple of questions and fill in the rest.",
};

const ToolIntakeModal = ({ onClose, onSaved }) => {
  const { showToast } = useToast();
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [pendingTool, setPendingTool] = useState(null);
  const [saving, setSaving] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !thinking) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, thinking]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || thinking) return;
    const userMsg = { role: 'user', content: text };
    const history = messages.filter((m) => m.role !== 'system');
    const nextMessages = [...history, userMsg];
    setMessages([...messages, userMsg, { role: 'assistant', content: '' }]);
    setInput('');
    setThinking(true);
    setPendingTool(null);

    try {
      const response = await fetch('/api/intake/tool-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nextMessages.map((m) => ({ role: m.role, content: m.content })) }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedText = '';
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let evt;
          try { evt = JSON.parse(line.slice(6)); } catch { continue; }
          if (evt.type === 'text') {
            streamedText += evt.text;
            setMessages((prev) => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: streamedText };
              return updated;
            });
          } else if (evt.type === 'done') {
            setThinking(false);
            if (evt.done && evt.project) {
              setPendingTool(evt.project);
              // Replace streamed text (which includes the raw JSON block) with just the reply
              if (evt.reply != null) {
                setMessages((prev) => {
                  const updated = [...prev];
                  updated[updated.length - 1] = { role: 'assistant', content: evt.reply };
                  return updated;
                });
              }
            }
          } else if (evt.type === 'error') {
            setThinking(false);
            showToast(evt.message || 'AI error', 'error');
          }
        }
      }
    } catch (err) {
      setThinking(false);
      showToast(err.message || 'Connection error', 'error');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const saveTool = async () => {
    if (!pendingTool || saving) return;
    setSaving(true);
    try {
      const { data } = await createTool(pendingTool);
      onSaved(data);
      onClose();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
      setSaving(false);
    }
  };

  const CAT_LABEL = (c) => c ? c.charAt(0).toUpperCase() + c.slice(1) : '';

  return (
    <div className="modal-overlay" onClick={() => !thinking && onClose()}>
      <div className="modal tool-intake-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>✨ Add Tool with AI</h2>
          <button className="modal-close" onClick={onClose} disabled={thinking}>✕</button>
        </div>

        <div className="tool-intake-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`fr-message fr-message-${msg.role}`}>
              {msg.content || (thinking && i === messages.length - 1
                ? <span className="fr-thinking">thinking…</span>
                : null)}
            </div>
          ))}

          {pendingTool && (
            <div className="fr-pending-card">
              <p className="fr-pending-label">Ready to add:</p>
              <p className="fr-pending-title">{pendingTool.name}</p>
              <div className="tool-intake-preview-meta">
                {pendingTool.brand && <span>{pendingTool.brand}</span>}
                {pendingTool.modelNumber && <span>{pendingTool.modelNumber}</span>}
                {pendingTool.category && <span>{CAT_LABEL(pendingTool.category)}</span>}
                {pendingTool.condition && <span>{CAT_LABEL(pendingTool.condition)} condition</span>}
                {pendingTool.location && <span>📍 {pendingTool.location}</span>}
              </div>
              {pendingTool.notes && <p className="fr-pending-desc">{pendingTool.notes}</p>}
              <div className="fr-pending-actions">
                <button className="btn btn-primary" onClick={saveTool} disabled={saving}>
                  {saving ? 'Adding…' : 'Add to Inventory'}
                </button>
                <button className="btn btn-secondary" onClick={() => { setPendingTool(null); inputRef.current?.focus(); }}>
                  Keep chatting
                </button>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        <div className="fr-input-row">
          <textarea
            ref={inputRef}
            className="fr-input"
            rows={2}
            placeholder="e.g. Milwaukee M18 drill, DeWalt circular saw…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={thinking}
            autoFocus
          />
          <button className="btn btn-primary fr-send-btn" onClick={send} disabled={thinking || !input.trim()}>
            {thinking ? '…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ToolIntakeModal;
